"use client";

import { Formik, Form, Field, ErrorMessage } from "formik";
import Link from "next/link";
import {
  useResetPassword,
  useResetPasswordWithYearOfBirth,
  useLogin,
} from "@/api/hook/useAuth";
import Header from "@/components/layout/Header";
import * as Yup from "yup";
import { useState } from "react";
import toast from "react-hot-toast";
import { Check, Clipboard } from "lucide-react";
import { useRouter } from "next/navigation";

const StudentResetPasswordSchema = Yup.object().shape({
  email: Yup.string().when("resetMethod", {
    is: (val: string) => val === "email" || val === "dob",
    then: (schema) =>
      schema.email("Invalid email address").required("Email is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  yearOfBirth: Yup.string().when("resetMethod", {
    is: "dob",
    then: (schema) => Yup.string().required("Year of birth is required"),
    otherwise: (schema) => Yup.string().notRequired(),
  }),
  resetMethod: Yup.string().required("Please select a reset method"),
});

type PasswordResetResponse = {
  message: string;
  response?: string;
  coded?: string;
};

const ForgotPasswordPage = () => {
  const resetPasswordEmail = useResetPassword();
  const router = useRouter();
  const resetPasswordWithDOB = useResetPasswordWithYearOfBirth();
  const loginMutation = useLogin();

  const [copied, setCopied] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(
    null,
  );
  const [resetEmail, setResetEmail] = useState<string>("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleCopy = () => {
    if (generatedPassword) {
      navigator.clipboard.writeText(generatedPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 10000);
      toast.success("Password copied to clipboard!");
    }
  };

  const handleLoginToDashboard = async () => {
    if (!resetEmail || !generatedPassword) return;
    setIsLoggingIn(true);
    try {
      await loginMutation.mutateAsync({
        email: resetEmail,
        password: generatedPassword,
      });
      toast.success("Login successful! Redirecting to dashboard...");
      setGeneratedPassword(null);
      router.push("/dashboard");
      router.refresh();
    } catch (error) {
      console.error("Auto-login after password reset failed:", error);
      toast.error("Login failed. Please sign in manually.");
      router.push("/sign-in");
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <>
      <Header />
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left Section */}
        <div className="w-full lg:w-2/5 bg-[#1C1B4D] flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-20 py-12">
          <h1 className="font-bold text-[#FFF] text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">
            Start learning
          </h1>
          <h1
            className="font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl mt-2"
            style={{
              backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              display: "inline-block",
            }}
          >
            with La Plage Meta Verse
          </h1>
        </div>
        {/* Right Section */}
        <div className="w-full lg:w-3/5 flex justify-center items-center px-6 sm:px-10 py-12">
          <div className="w-full max-w-md">
            <h3 className="font-bold text-primary text-3xl sm:text-4xl text-center mb-8">
              Forgot Password!
            </h3>
            <p className="text-center text-[#4B5563] mb-6">
              Choose your preferred method to reset your password
            </p>
            <Formik
              initialValues={{ email: "", yearOfBirth: "", resetMethod: "" }}
              validationSchema={StudentResetPasswordSchema}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  if (values.resetMethod === "email") {
                    const response: PasswordResetResponse =
                      await resetPasswordEmail.mutateAsync({
                        email: values.email,
                      });
                    if (response.coded) {
                      setResetEmail(values.email);
                      setGeneratedPassword(response.coded);
                      toast.success(
                        "New Password Generated Successfully via Email!",
                      );
                    } else {
                      toast.success(
                        response?.message ||
                          "Password reset instructions sent to your email.",
                      );
                    }
                  } else if (values.resetMethod === "dob") {
                    const year = Number.parseInt(values.yearOfBirth || "", 10);
                    if (isNaN(year)) {
                      toast.error("Year of birth must be a valid number.");
                      return;
                    }
                    const response: PasswordResetResponse =
                      await resetPasswordWithDOB.mutateAsync({
                        email: values.email,
                        yearOfBirth: year,
                      });
                    if (response) {
                      setResetEmail(values.email);
                      setGeneratedPassword(response as unknown as string);
                      toast.success(
                        "New Password Generated Successfully with DOB!",
                      );
                    } else {
                      toast.error("Failed to generate new password with DOB.");
                    }
                  } else {
                    toast.error("Please select a valid password reset method.");
                    return;
                  }
                } catch (error) {
                  console.error("Password reset error:", error);
                  toast.error(
                    "An error occurred during password reset. Please try again.",
                  );
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ isSubmitting, errors, touched, values, setFieldValue }) => (
                <Form className="flex flex-col gap-6">
                  {/* Reset Method Selection */}
                  <div className="flex flex-col gap-3">
                    <label className="font-medium text-lg sm:text-xl text-primary">
                      Choose reset method:
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="resetMethod"
                          value="email"
                          checked={values.resetMethod === "email"}
                          onChange={(e) => {
                            setFieldValue("resetMethod", e.target.value);
                            setFieldValue("yearOfBirth", "");
                          }}
                          className="w-4 h-4 text-primary bg-[#F3F4F6] border-gray-300 focus:ring-2 focus:ring-primary"
                        />
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-700">
                            Enter Email To Generate New Password
                          </span>
                          <span className="text-xs text-[#6B7280]">
                            Get a new password instantly with your email
                          </span>
                        </div>
                      </label>
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="resetMethod"
                          value="dob"
                          checked={values.resetMethod === "dob"}
                          onChange={(e) => {
                            setFieldValue("resetMethod", e.target.value);
                          }}
                          className="w-4 h-4 text-primary bg-[#F3F4F6] border-gray-300 focus:ring-2 focus:ring-primary"
                        />
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-700">
                            Generate New Password Using Date of Birth
                          </span>
                          <span className="text-xs text-[#6B7280]">
                            Get a new password instantly with email + birth year
                          </span>
                        </div>
                      </label>
                    </div>
                    <ErrorMessage
                      name="resetMethod"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  {/* Email Field - Show for both methods */}
                  {(values.resetMethod === "email" ||
                    values.resetMethod === "dob") && (
                    <div className="flex flex-col gap-3">
                      <label className="font-medium text-lg sm:text-xl text-primary">
                        Email Address
                      </label>
                      <Field
                        type="email"
                        name="email"
                        className={`rounded-md py-3 px-4 border ${
                          errors.email && touched.email
                            ? "border-red-500"
                            : "border-black"
                        }`}
                        placeholder="Enter your email address"
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-red-500 text-sm"
                      />
                    </div>
                  )}
                  {/* Date of Birth Field - Only show when DOB method is selected */}
                  {values.resetMethod === "dob" && (
                    <div className="flex flex-col gap-3">
                      <label className="font-medium text-lg sm:text-xl text-primary">
                        Year of Birth
                      </label>
                      <Field
                        type="number"
                        name="yearOfBirth"
                        className={`rounded-md py-3 px-4 border ${
                          errors.yearOfBirth && touched.yearOfBirth
                            ? "border-red-500"
                            : "border-black"
                        }`}
                        placeholder="Enter your year of birth (e.g., 1993)"
                      />
                      <ErrorMessage
                        name="yearOfBirth"
                        component="div"
                        className="text-red-500 text-sm"
                      />
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      resetPasswordEmail.isPending ||
                      resetPasswordWithDOB.isPending ||
                      !values.resetMethod
                    }
                    className="bg-primary rounded-md text-[#FFF] text-lg sm:text-xl font-bold py-3 hover:bg-primary/90 disabled:opacity-50"
                  >
                    {isSubmitting ||
                    resetPasswordEmail.isPending ||
                    resetPasswordWithDOB.isPending
                      ? "Loading..."
                      : "Continue"}
                  </button>
                  <Link
                    href="/sign-in"
                    className="text-lg sm:text-xl text-center text-primary hover:text-primary/80"
                  >
                    Back to Login
                  </Link>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* Generated Password Modal - Show for both methods if password is generated */}
      {generatedPassword && (
        <div className="fixed inset-0 bg-[#1E1E1E]/80 flex items-center justify-center z-50 px-4">
          <div className="bg-[#FFF] rounded-xl shadow-xl p-6 w-full max-w-sm space-y-4 text-center relative">
            <h3 className="text-lg font-semibold text-gray-900">
              Your New Password
            </h3>
            <p className="text-red-500 mb-3 text-sm">
              Please save this password securely. You can use it to sign in
              immediately.
            </p>

            {/* Credentials box - email + password */}
            <div className="bg-[#F3F4F6] rounded-md p-3 border space-y-2 text-left">
              <div>
                <p className="text-xs font-medium text-[#6B7280]">
                  Email Address
                </p>
                <p className="text-gray-900 text-sm font-semibold break-all">
                  {resetEmail}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-[#6B7280]">
                  New Password
                </p>
                <p className="text-gray-900 font-mono text-sm break-all select-all">
                  {generatedPassword}
                </p>
              </div>
            </div>

            <button
              onClick={handleCopy}
              className="flex items-center justify-center w-full py-2 px-4 rounded-lg bg-[#49489b] text-[#FFF] transition"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" /> Copied!
                </>
              ) : (
                <>
                  <Clipboard className="w-4 h-4 mr-2" /> Copy Password
                </>
              )}
            </button>

            <button
              onClick={handleLoginToDashboard}
              disabled={isLoggingIn || loginMutation.isPending}
              className="w-full py-2 px-4 rounded-lg bg-[#028900] text-[#FFF] hover:bg-green-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingIn || loginMutation.isPending
                ? "Logging in..."
                : "Login to Dashboard"}
            </button>

            <button
              onClick={() => setGeneratedPassword(null)} // Just close the modal
              className="w-full py-2 px-4 rounded-lg bg-[#ff0000] text-[#FFF] hover:bg-[#374151] transition text-sm"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ForgotPasswordPage;
